<template>
  <div class="ranked-providers-list">
    <h2 class="headline">{{headline}}</h2>
    <v-card class="mt-2">
      <v-layout row wrap justify-space-between align-center class="ranked-providers-list__header pa-2" >
        <v-flex xs12>
          <v-text-field
            v-model="filter"
            class="ranked-providers-list__header-search hidden-md-and-up mb-2"
            placeholder="Find by name"
            :append-icon="searchTerm ? 'close' : 'search'"
            :solo="true"
            hide-details
            @click:append="searchTriggered"
            @keydown.native.enter.stop.prevent="filterProviders"
          /> <!--The keydown event to be prevented and stopped otherwise IE10 triggers button clicks on the page. -->
        </v-flex>
        <v-flex xs8>
          <span class="ranked-providers-list__rank">Rank</span>
          <span class="ranked-providers-list__logo">Company</span>
          <v-text-field
            v-model="filter"
            class="ranked-providers-list__header-search hidden-sm-and-down custom-padding"
            placeholder="Find by name"
            :append-icon="searchTerm ? 'close' : 'search'"
            :solo="true"
            hide-details
            @click:append="searchTriggered"
            @keydown.native.enter.stop.prevent="filterProviders"
          />
        </v-flex>
        <transition name="fade-transition">
          <v-flex xs4 v-if="!hideUserShare">
            <span :title="shareTooltip">Bill Payment Market Share</span>
            <help-tip :top="true" class="hidden-xs-only ml-1">{{ shareTooltip }}</help-tip>
          </v-flex>
        </transition>
      </v-layout> <!-- END: ranked-providers-list__header -->

      <transition name="fade-transition">
        <v-alert v-if="showError" type="error" value="true" dismissible class="py-2 px-3 my-0" @click="showError=false">
          Failed to load companies.
        </v-alert>
        <v-alert v-if="providersSize==0 && !loading && allProvidersLoaded" type="warning" :value="true" class="py-2 px-3 my-0" >
          {{(searchTerm && allProvidersSize()>0)? `'${searchTerm}' is not among the top companies.`: `No ${companies()} are followed in the selected area.`}}
        </v-alert>
      </transition> <!-- END: alerts -->

      <providers-list-item
        v-for="p in providersDetails"
        :key="p.id"
        :provider="p"
        :hideUserShare="hideUserShare"
        :geo-name="geoName"
      />

      <v-layout wrap mt-3>
        <v-flex
          v-if="showShowMoreBtn()"
          xs8 offset-xs2
          sm4 offset-sm4
          text-xs-center
        >
          <v-btn large color="primary" class="mb-1" @click="showMoreProviders" :disabled="!!loading">Show More&hellip;</v-btn>
        </v-flex>
        <v-flex xs12 text-xs-center>
          <p class="mt-1 mb-3">{{totalProviders}} total {{companies()}} in {{geoName()}}</p>
        </v-flex>
      </v-layout> <!-- END: footer -->
    </v-card>
  </div>
</template>

<script>
  import { eventBus } from '@/utils'
  import ProvidersListItem from './ProvidersListItem'
  import axios from 'axios'
  import HelpTip from '@/components/common/HelpTip'
  import airbrake from '@/helpers/airbrake'

  const pageSize = 100

  /**
   * the minimum number of follows from complete lists to display the user share column.
   **/
  const minCompleteListFollowsCount = 3

  export default {
    components: {
      ProvidersListItem, HelpTip
    },

    mounted () {
      this.allProviders = []
      this.loadAllProviders()
      this.filterField = this.$el.querySelector('.search-field')
    },

    props: {
      serviceTypeName: {
        required: true,
        type: Function
      },
      geoName: {
        required: true,
        type: Function
      },
      apiPath: {
        required: true,
        type: Function
      },
      stats: {
        required: true,
        type: Object
      }
    },

    data () {
      return {
        loading: 0,
        showError: false,
        totalFollows: {complete: 0, incomplete: 0},
        minCompleteListFollowsCount: minCompleteListFollowsCount,
        providersDetails: [], // providers from follows of complete and incomplete doxo lists
        providersSize: 0, // the size of all providers or the size of the filtered providers. Some may not be displayed
        filter: '', // the value in the filter field
        searchTerm: '', // search term already applied to filter the list of providers
        allProvidersLoaded: false
      }
    },

    watch: {
      loading (isLoading) { // TODO [balazs] - make this work
        eventBus('progress-bar').$emit(isLoading ? 'start' : 'done')
      },

      $route () {
        this.loadAllProviders()
      }
    },

    methods: {
      searchTriggered () {
        if (this.searchTerm) { // X btn displayed
          this.clearFilter()
        } else { // search btn displayed
          if (!this.loading && this.allProvidersSize() > 0) {
            this.filterProviders()
          }
        }
      },
      allProvidersSize () {
        return this.allProviders ? this.allProviders.length : 0
      },

      companies () {
        const stName = this.serviceTypeName()
        return stName ? `${stName} companies` : 'companies'
      },

      clearFilter () {
        this.filter = ''
        this.filterProviders('')
      },

      filterProviders (term) {
        if (typeof term !== 'string') {
          term = this.filter
        }

        if (!this.loading && term !== this.searchTerm && this.allProvidersSize() > 0) { // filter only if the term changed
          this.searchTerm = term
          const clearProvidersDetails = function () {
            this.providersDetails.splice(0, this.providersDetails.length) // hiding each provider currently displayed
          }.bind(this)

          if (term) {
            const re = new RegExp(term, 'gi')
            this.filteredProviders = this.allProviders.filter(p => {
              return !!p.n.match(re)
            })
            this.providersSize = this.filteredProviders.length
          } else { // clearing the filter
            this.filteredProviders = null
            this.providersSize = this.allProviders.length
          }
          if (this.providersSize === 0) {
            clearProvidersDetails()
            return
          }

          // clearing the list is better after the XHR for a smoother UX.
          // It also fixes a weird minor bug that prevents from displaying the loading overlay on the list.
          const p = this.filteredProviders ? this.filteredProviders : this.allProviders
          this.loadProvidersDetails(0,
            (p.length < pageSize) ? p.length : pageSize,
            clearProvidersDetails)
        }
      },

      headerHeight () {
        if (document && document.getElementsByClassName('app-toolbar').length > 0) {
          return document.getElementsByClassName('app-toolbar')[0].offsetHeight
        }
        return 0
      },

      showMoreProviders () {
        if (this.showShowMoreBtn() && !this.loading) {
          this.loadProvidersDetails()
        }
      },

      /**
       * Since there is no aggregation bucket pagination in ES 1.5 this call can potentially fetch
       * elementary data of a large number of providers
       */
      loadAllProviders () {
        this.loading++
        this.showError = false

        axios.get(this.apiPath(this.$route, `/g_api/top_providers`)).then(response => {
          const payload = response.data
          this.allProviders = payload.providers
          this.providersSize = payload.providers.length
          this.totalFollows = payload.totalFollows
          this.loading--

          // resetting is needed when the top_providers API is called because the user
          // requested refreshing data on the page
          this.providersDetails.splice(0, this.providersDetails.length)
          this.filter = '' // TODO persist filter across queries
          this.searchTerm = ''
          this.filteredProviders = null

          if (this.allProviders.length > 0) {
            this.loadProvidersDetails()
          }
          this.allProvidersLoaded = true
        }).catch(this.onXhrFailure)
      },

      appendToList (providersDetails) {
        const page = []
        const all = this.filteredProviders || this.allProviders
        const totalCompleteFollows = this.totalFollows['complete']
        const from = this.providersDetails.length
        const topShare = all[0] ? (all[0].c / totalCompleteFollows) * 100 : 100 // 100 helps avoid division by zero

        const _to = this.to()
        for (let i = from; i < _to; i++) {
          const provider = all[i]
          const fromCompleteList = provider.cl
          const details = providersDetails[provider.id]
          const share = fromCompleteList ? (provider.c / totalCompleteFollows) * 100 : 0
          const relativeShare = fromCompleteList ? (share / topShare) * 100 : 0
          const providerDetails = {
            slug: details ? details.slug : undefined,
            rank: provider.r,
            tie: i > 0 && provider.c === all[i - 1].c,
            id: provider.id,
            name: provider.n,
            logoUrl: details ? details.logo : undefined,
            payable: details ? details.payable : undefined,
            barWidth: `${share >= 0.1 ? relativeShare : 0}%`,
            share: share < 0.5 ? '< 0.5 %' : `${share.toFixed(1)} %`
          }
          page.push(providerDetails)
        }

        Array.prototype.push.apply(this.providersDetails, page)
      },

      onXhrFailure (err) {
        this.loading--
        this.showError = true
        airbrake.notify(err)
      },

      to (max = pageSize) {
        const all = this.filteredProviders || this.allProviders
        const from = this.providersDetails.length
        return all.length > from + max ? from + max : all.length
      },

      loadProvidersDetails (from, to, onSuccess) {
        let all = this.filteredProviders || this.allProviders
        const displayed = this.providersDetails
        const ids = []
        if (from === undefined) {
          from = displayed.length
        }

        if (from < all.length) { // not all providers are displayed yet
          const _to = to === undefined ? this.to() : to
          for (let i = from; i < _to; i++) {
            const providerId = all[i].id
            ids.push(providerId)
          }
        }

        if (ids.length > 0) {
          this.loading++
          axios
            .get(`/g_api/providers_details/${ids.toString()}`).then(response => {
              const payload = response.data
              this.loading--
              if (onSuccess) {
                onSuccess()
              }
              const providersDetails = {}
              ids.forEach(id => { providersDetails[id] = payload[id] })

              if (ids.length > 0) {
                this.appendToList(providersDetails)
              }
            })
            .catch(this.onXhrFailure)
        }
      },

      showShowMoreBtn () {
        return this.providersSize > this.providersDetails.length
      }
    },

    computed: {
      headline () {
        const stName = this.serviceTypeName() ? `${this.serviceTypeName()}: ` : ''
        return `${stName}Most common bills paid by customers in ${this.geoName()}`
      },

      totalProviders () {
        const counters = this.stats.providerCounters
        return counters ? counters.total : '0' // The stats call on the BE may fail (timeout)
      },

      shareTooltip () {
        return `Share of doxo users in ${this.geoName()} that are customers of the listed company`
      },

      hideUserShare () {
        return this.totalFollows.complete < minCompleteListFollowsCount
      }
    }
  }
</script>

<style lang="stylus">
  @require '~styles/bonsai/_settings'

  .ranked-providers-list {
    &__header {
      background: #eee
      font-weight: bold
      font-size: 16px

      &-search {
        min-height: initial !important
        @media $display-breakpoints.md-and-up {
          width: 400px
          display: inline-block
        }
        .input-group__input {
          padding: 4px 4px 4px 16px !important
        }
      }
    }

    &__rank {
      width: 60px
      min-width: 60px
      max-width: 60px
      text-align: center
      display: inline-block
      @media $display-breakpoints.xs-only {
        width: 50px
        min-width: 50px
        max-width: 50px
      }
    }

    &__logo {
      display inline-block
      width: 110px
      text-align: center
      @media $display-breakpoints.xs-only {
        text-align: initial
        padding-left: 10px
      }
    }

    .custom-padding {
      padding-left: 24px;
    }
  }
</style>



// WEBPACK FOOTER //
// frontend/src/components/common/ProvidersList.vue